export type UserInfoStatus = 'loading' | 'authenticated' | 'anonymous' | 'error';

export interface ImgCloudUserConfig extends Record<string, unknown> {
  allowJsUpload?: boolean | string;
  domains?: string;
  enableAiUrlFetch?: boolean | string;
  enableImageSearch?: boolean;
  enableUploadPrewarm?: boolean | string;
  host?: string;
  imgViewer?: string;
  lifecycleDays?: string;
  watermask?: string;
}

export interface ImgCloudUserInfo extends Record<string, unknown> {
  config: ImgCloudUserConfig;
  contextPath?: string;
  dailyMaxUploadCount?: number | string | null;
  domains?: string[];
  email: string;
  isActiveVip: boolean;
  username: string;
  vip?: boolean;
  vipEndTime?: string;
  vipLevel?: string;
}

export interface UserInfoSnapshot {
  config: ImgCloudUserConfig;
  dailyMaxUploadCount: number | string | null;
  enableAiUrlFetch: boolean;
  status: 'authenticated' | 'anonymous';
  user: ImgCloudUserInfo | null;
}

interface LoginInfoResponse extends Record<string, unknown> {
  dailyMaxUploadCount?: unknown;
  detail?: unknown;
  email?: unknown;
  enableAiUrlFetch?: unknown;
  isSuccess?: unknown;
  message?: unknown;
  success?: unknown;
}

interface InFlightRequest {
  force: boolean;
  generation: number;
  promise: Promise<UserInfoSnapshot>;
}

const EMPTY_CONFIG: ImgCloudUserConfig = Object.freeze({});

let cachedSnapshot: UserInfoSnapshot | null = null;
let inFlightRequest: InFlightRequest | null = null;
let cacheGeneration = 0;

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const readString = (value: unknown): string => (
  typeof value === 'string' ? value : ''
);

const readConfig = (value: unknown): ImgCloudUserConfig => {
  if (isRecord(value)) {
    return { ...value };
  }
  if (typeof value !== 'string' || value.trim() === '') {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(value);
    if (isRecord(parsed)) {
      return { ...parsed };
    }
  } catch (error) {
    console.error('Failed to parse img-cloud user config:', error);
    return {};
  }

  console.error('Failed to parse img-cloud user config: expected a JSON object');
  return {};
};

const isAnonymousResponse = (response: Response, payload: LoginInfoResponse): boolean => {
  if (response.status === 401 || response.status === 403) {
    return true;
  }
  if (response.status === 400 && payload.code === 'login.userInfo.fail') {
    return true;
  }
  return (payload.isSuccess === false || payload.success === false) && response.ok !== false;
};

const normalizeLoginInfo = (response: Response, payload: unknown): UserInfoSnapshot => {
  if (!isRecord(payload)) {
    throw new Error('User information response is not a JSON object');
  }

  const loginInfo = payload as LoginInfoResponse;
  const detail = isRecord(loginInfo.detail) ? loginInfo.detail : {};
  const userFields: Record<string, unknown> = { ...loginInfo, ...detail };
  delete userFields.detail;
  delete userFields.isSuccess;
  delete userFields.success;
  delete userFields.message;
  delete userFields.code;

  const email = readString(loginInfo.email) || readString(userFields.email) || readString(userFields.username);
  const username = readString(userFields.username) || email;
  const authenticated = loginInfo.isSuccess === true && Boolean(email || username);

  if (!authenticated) {
    if (isAnonymousResponse(response, loginInfo)) {
      return {
        config: EMPTY_CONFIG,
        dailyMaxUploadCount: null,
        enableAiUrlFetch: false,
        status: 'anonymous',
        user: null,
      };
    }
    throw new Error(readString(loginInfo.message) || `Failed to load user information (${response.status || 'unknown'})`);
  }

  if (response.ok === false) {
    throw new Error(readString(loginInfo.message) || `Failed to load user information (${response.status})`);
  }

  const config = readConfig(userFields.config);
  const dailyMaxUploadCount = (
    typeof loginInfo.dailyMaxUploadCount === 'number' || typeof loginInfo.dailyMaxUploadCount === 'string'
      ? loginInfo.dailyMaxUploadCount
      : typeof userFields.dailyMaxUploadCount === 'number' || typeof userFields.dailyMaxUploadCount === 'string'
        ? userFields.dailyMaxUploadCount
        : null
  );
  const enableAiUrlFetch = loginInfo.enableAiUrlFetch === true
    || userFields.enableAiUrlFetch === true
    || config.enableAiUrlFetch === true
    || config.enableAiUrlFetch === 'true';
  const user: ImgCloudUserInfo = {
    ...userFields,
    config,
    dailyMaxUploadCount,
    email,
    isActiveVip: userFields.isActiveVip === true,
    username,
  };

  return {
    config,
    dailyMaxUploadCount,
    enableAiUrlFetch,
    status: 'authenticated',
    user,
  };
};

const requestUserInfo = async (): Promise<UserInfoSnapshot> => {
  const response = await fetch('/login/info', {
    method: 'GET',
    credentials: 'include',
  });
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new Error(`Failed to parse user information response (${response.status || 'unknown'})`);
  }
  return normalizeLoginInfo(response, payload);
};

export const loadUserInfo = (options: { force?: boolean } = {}): Promise<UserInfoSnapshot> => {
  const force = options.force === true;
  if (!force && cachedSnapshot) {
    return Promise.resolve(cachedSnapshot);
  }
  if (inFlightRequest && (!force || inFlightRequest.force)) {
    return inFlightRequest.promise;
  }

  const generation = force ? ++cacheGeneration : cacheGeneration;
  const promise = requestUserInfo()
    .then((snapshot) => {
      if (generation === cacheGeneration) {
        cachedSnapshot = snapshot;
      }
      return snapshot;
    })
    .finally(() => {
      if (inFlightRequest?.promise === promise) {
        inFlightRequest = null;
      }
    });

  inFlightRequest = { force, generation, promise };
  return promise;
};

export const getCachedUserInfo = (): UserInfoSnapshot | null => cachedSnapshot;

export const clearUserInfoCache = (): void => {
  cacheGeneration += 1;
  cachedSnapshot = null;
  inFlightRequest = null;
};
